import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check, MessageCircleHeart, BookOpen } from "lucide-react";
export default function BibleBook() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [read, setRead] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bible/books/${slug}`).then((r) => setBook(r.data));
    api.get("/bible/read").then((r) => setRead(r.data.includes(slug)));
  }, [slug]);

  if (!book) return null;

  const toggleRead = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bible/books/${slug}/read`);
      setRead(data.explored);
      toast.success(data.explored ? `${book.name} marked as explored.` : "Removed from explored.");
    } catch { toast.error("Could not update."); }
    finally { setBusy(false); }
  };

  const askGuide = () => {
    navigate(`/guide?prefill=${encodeURIComponent(`Help me understand the book of ${book.name}. What is its message and how can it shape my walk with God?`)}`);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <button data-testid="book-back-button" onClick={() => navigate("/scripture")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-12">
        <ArrowLeft className="w-4 h-4" /> All books
      </button>

      <div className="animate-fade-up">
        <p className="label-overline mb-3">{book.testament} Testament · {book.category}</p>
        <h1 className="font-heading text-5xl sm:text-6xl tracking-tight leading-none" data-testid="book-title">{book.name}</h1>
        <p className="text-sm text-muted-foreground mt-4">{book.chapters} chapters</p>

        <p className="text-lg leading-relaxed mt-10 text-foreground/90">{book.summary}</p>

        <div className="border-l-2 border-primary/20 pl-6 mt-10">
          <p className="label-overline mb-2">Key Verse</p>
          <p className="font-scripture italic text-2xl text-primary">{book.key_verse}</p>
        </div>

        <div className="mt-10">
          <p className="label-overline mb-3">Themes</p>
          <div className="flex flex-wrap gap-2">
            {book.themes.map((t) => (
              <span key={t} className="text-sm px-4 py-1.5 rounded-full bg-muted text-secondary-foreground">{t}</span>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-14">
          <button data-testid="read-book-button" onClick={() => navigate(`/scripture/${slug}/read`)}
            className="flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm px-6 py-3.5 hover:bg-primary/90 transition-colors">
            <BookOpen className="w-4 h-4" /> Read {book.name}
          </button>
          <button data-testid="mark-read-button" onClick={toggleRead} disabled={busy}
            className={`flex items-center justify-center gap-2 rounded-sm px-6 py-3.5 transition-colors disabled:opacity-60 ${read ? "bg-muted text-foreground border border-border" : "border border-border hover:bg-muted"}`}>
            <Check className="w-4 h-4" /> {read ? "Explored" : "Mark as explored"}
          </button>
          <button data-testid="ask-guide-button" onClick={askGuide}
            className="flex items-center justify-center gap-2 border border-border rounded-sm px-6 py-3.5 hover:bg-muted transition-colors">
            <MessageCircleHeart className="w-4 h-4 text-accent" /> Reflect with the Guide
          </button>
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-6">
          <BookOpen className="w-3.5 h-3.5" /> Full text available in KJV and the World English Bible (WEB).
        </div>
      </div>
    </div>
  );
}
